import React, { useState, useCallback } from 'react';
import { X, Loader2, Wand2, Send, RefreshCw, Mail, Users, Building2, Zap, Edit3, Check, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, XCircle, Trash2, Shield, Clock } from 'lucide-react';
import { fetchWithAuth, API_URL } from './constants';
import { toast } from 'sonner';

const TONES = ['professional', 'conversational', 'direct', 'consultative'];

/* ── Step: Selection Summary ── */
const SelectionSummary = ({ preflight, onRemove, onProceed, generating }) => {
  const safe = preflight.filter(c => c.safe);
  const warned = preflight.filter(c => c.warning && c.safe);
  const unsafe = preflight.filter(c => !c.safe);

  return (
    <div className="space-y-4" data-testid="batch-selection-summary">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
          <span className="text-xs font-bold text-emerald-700">{safe.length} ready</span>
        </div>
        {unsafe.length > 0 && (
          <div className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-xs font-bold text-red-700">{unsafe.length} excluded</span>
          </div>
        )}
        {warned.length > 0 && (
          <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-xs font-bold text-amber-700">{warned.length} with warnings</span>
          </div>
        )}
      </div>

      <div className="max-h-64 overflow-y-auto space-y-1.5">
        {preflight.map((c, i) => (
          <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${c.safe ? 'bg-white border-gray-200' : 'bg-red-50 border-red-200 opacity-60'}`}
            data-testid={`preflight-${c.company}`}>
            {c.safe ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-[#0A1F44] truncate">{c.company}</span>
                <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded border ${c.tier === 'high_fit' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                  {c.tier === 'high_fit' ? 'BEST IMMEDIATE' : 'BROADER QUALIFIED'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5">
                <span>{c.industry || 'Unknown'}</span>
                <span>{c.employee_count ? `${c.employee_count.toLocaleString()} emp` : '—'}</span>
                <span>Score: {c.auto_score}</span>
                <span>Confidence: {c.confidence}</span>
                {c.contacts_count > 0 && <span className="text-emerald-600">{c.contacts_count} contacts</span>}
                <span className={`px-1 py-0.5 rounded ${c.work_status === 'none' ? 'bg-gray-100' : 'bg-blue-50 text-blue-600'}`}>{c.work_status}</span>
              </div>
            </div>
            {c.warning && (
              <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded shrink-0 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />{c.warning}
              </span>
            )}
            <button onClick={() => onRemove(c.company)} className="p-1 hover:bg-gray-100 rounded shrink-0" data-testid={`remove-${c.company}`}>
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          </div>
        ))}
      </div>

      <button onClick={onProceed} disabled={safe.length === 0 || generating}
        className="w-full py-2.5 bg-[#0A1F44] text-white text-xs font-bold rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="batch-generate-btn">
        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
        Generate Sequences for {safe.length} Companies
      </button>
    </div>
  );
};

/* ── Step: Review Queue ── */
const ReviewQueue = ({ drafts, onApprove, onSkip, onEdit, onRegenerate, expandedDraft, setExpandedDraft, approvedSet, skippedSet, tone, setTone, steps, setSteps }) => (
  <div className="space-y-2" data-testid="batch-review-queue">
    {/* Controls */}
    <div className="flex items-center gap-3 flex-wrap mb-2">
      <select value={tone} onChange={e => setTone(e.target.value)}
        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" data-testid="batch-tone">
        {TONES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
      </select>
      <select value={steps} onChange={e => setSteps(Number(e.target.value))}
        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white" data-testid="batch-steps">
        <option value={2}>2 steps</option>
        <option value={3}>3 steps</option>
        <option value={4}>4 steps</option>
        <option value={5}>5 steps</option>
      </select>
      <div className="flex-1" />
      <div className="flex items-center gap-2 text-[10px]">
        <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full font-semibold">{approvedSet.size} approved</span>
        <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded-full">{skippedSet.size} skipped</span>
        <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded-full">{drafts.length - approvedSet.size - skippedSet.size} pending</span>
      </div>
    </div>

    {/* Draft cards */}
    <div className="max-h-[50vh] overflow-y-auto space-y-2">
      {drafts.map((draft, di) => {
        const isExpanded = expandedDraft === di;
        const isApproved = approvedSet.has(di);
        const isSkipped = skippedSet.has(di);
        const allSteps = draft.sequence?.sequences?.[0]?.steps || [];

        return (
          <div key={di} className={`border rounded-xl overflow-hidden transition-all ${isApproved ? 'border-emerald-300 bg-emerald-50/30' : isSkipped ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-gray-200 bg-white'}`}
            data-testid={`batch-draft-${di}`}>
            <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={() => setExpandedDraft(isExpanded ? null : di)}>
              <div className="w-6 h-6 rounded-full bg-[#0A1F44] text-white flex items-center justify-center text-[10px] font-bold shrink-0">{di + 1}</div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-bold text-[#0A1F44]">{draft.company}</span>
                {draft.status === 'error' && <span className="ml-2 text-[10px] text-red-600">Generation failed</span>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!isSkipped && (
                  <button onClick={(e) => { e.stopPropagation(); onApprove(di); }}
                    className={`px-2.5 py-1 text-[10px] font-semibold rounded-full border flex items-center gap-1 ${isApproved ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}
                    data-testid={`approve-${di}`}>
                    <Check className="h-3 w-3" />{isApproved ? 'Approved' : 'Approve'}
                  </button>
                )}
                {!isApproved && (
                  <button onClick={(e) => { e.stopPropagation(); onSkip(di); }}
                    className={`px-2.5 py-1 text-[10px] font-semibold rounded-full border ${isSkipped ? 'bg-gray-300 text-white border-gray-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                    data-testid={`skip-${di}`}>
                    {isSkipped ? 'Skipped' : 'Skip'}
                  </button>
                )}
                {!isSkipped && (
                  <button onClick={(e) => { e.stopPropagation(); onRegenerate(di); }}
                    className="p-1.5 hover:bg-gray-100 rounded" data-testid={`regen-${di}`}>
                    <RefreshCw className="h-3 w-3 text-gray-400" />
                  </button>
                )}
                {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
              </div>
            </div>

            {isExpanded && draft.status === 'generated' && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-2">
                {allSteps.map((step, si) => {
                  const v = step.variants?.[0] || {};
                  return (
                    <div key={si} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-[#0A1F44]">Step {si + 1}</span>
                        <span className="text-[9px] text-gray-400">{step.delay} {step.delay_unit}</span>
                      </div>
                      <p className="text-[10px] text-gray-500 mb-0.5">Subject: <span className="text-[#0A1F44] font-medium">{v.subject}</span></p>
                      <div className="text-[10px] text-gray-600 whitespace-pre-wrap leading-relaxed max-h-20 overflow-y-auto">{v.body}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

/* ── Step: Results ── */
const ResultsSummary = ({ results, onClose }) => {
  const sent = results.filter(r => r.status === 'sent');
  const skipped = results.filter(r => r.status === 'skipped');

  return (
    <div className="space-y-4" data-testid="batch-results">
      <div className="text-center py-4">
        <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
        <h3 className="text-base font-bold text-[#0A1F44]">Batch Outreach Complete</h3>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
          <p className="text-lg font-bold text-emerald-700">{sent.length}</p>
          <p className="text-[10px] text-emerald-600 uppercase">Sent</p>
        </div>
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-center">
          <p className="text-lg font-bold text-gray-600">{skipped.length}</p>
          <p className="text-[10px] text-gray-500 uppercase">Skipped</p>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-center">
          <p className="text-lg font-bold text-blue-600">{sent.filter(s => s.autopilot_applied).length}</p>
          <p className="text-[10px] text-blue-500 uppercase">Auto-updated</p>
        </div>
      </div>

      <div className="max-h-48 overflow-y-auto space-y-1">
        {results.map((r, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50" data-testid={`result-${i}`}>
            {r.status === 'sent' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" /> : <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
            <span className="text-xs text-[#0A1F44] font-medium">{r.company}</span>
            <span className="flex-1" />
            {r.status === 'sent' && r.autopilot_applied && (
              <span className="text-[9px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{r.old_status} → {r.new_status}</span>
            )}
            <span className={`text-[10px] font-semibold ${r.status === 'sent' ? 'text-emerald-600' : 'text-gray-500'}`}>{r.status}</span>
          </div>
        ))}
      </div>

      <button onClick={onClose}
        className="w-full py-2.5 bg-[#0A1F44] text-white text-xs font-bold rounded-lg hover:bg-[#0d2a5c]"
        data-testid="batch-close-btn">
        Close & Refresh
      </button>
    </div>
  );
};

/* ── Main Modal ── */
export const BatchOutreachModal = ({ selectedCompanies, onClose, onComplete }) => {
  const [step, setStep] = useState('preflight'); // preflight -> review -> confirm -> results
  const [preflight, setPreflight] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [tone, setTone] = useState('professional');
  const [numSteps, setNumSteps] = useState(3);
  const [expandedDraft, setExpandedDraft] = useState(null);
  const [approvedSet, setApprovedSet] = useState(new Set());
  const [skippedSet, setSkippedSet] = useState(new Set());

  // Run preflight on mount
  const runPreflight = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/batch-outreach/preflight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companies: selectedCompanies }),
      });
      setPreflight(data.companies || []);
    } catch { toast.error('Preflight check failed'); }
    setLoading(false);
  }, [selectedCompanies]);

  React.useEffect(() => { runPreflight(); }, [runPreflight]);

  const removePreflight = (name) => {
    setPreflight(prev => prev.filter(c => c.company !== name));
  };

  const handleGenerate = async () => {
    const safe = preflight.filter(c => c.safe).map(c => c.company);
    if (safe.length === 0) return;

    setGenerating(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/batch-outreach/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companies: safe, tone, num_steps: numSteps }),
      });
      setDrafts(data.results || []);
      setStep('review');
    } catch { toast.error('Batch generation failed'); }
    setGenerating(false);
  };

  const handleApprove = (idx) => {
    setApprovedSet(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
    setSkippedSet(prev => { const next = new Set(prev); next.delete(idx); return next; });
  };

  const handleSkip = (idx) => {
    setSkippedSet(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
    setApprovedSet(prev => { const next = new Set(prev); next.delete(idx); return next; });
  };

  const handleApproveAll = () => {
    const all = new Set();
    drafts.forEach((d, i) => { if (d.status === 'generated') all.add(i); });
    setApprovedSet(all);
    setSkippedSet(new Set());
  };

  const handleRegenerate = async (idx) => {
    const name = drafts[idx]?.company;
    if (!name) return;
    toast.info(`Regenerating for ${name}...`);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/batch-outreach/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companies: [name], tone, num_steps: numSteps }),
      });
      if (data.results?.[0]) {
        setDrafts(prev => prev.map((d, i) => i === idx ? data.results[0] : d));
        toast.success(`Regenerated for ${name}`);
      }
    } catch { toast.error('Regeneration failed'); }
  };

  const handleSendApproved = async () => {
    if (approvedSet.size === 0) { toast.error('No approved items to send'); return; }
    setSending(true);
    const items = [];
    approvedSet.forEach(idx => {
      const d = drafts[idx];
      if (d?.status === 'generated') items.push({ company_name: d.company });
    });
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/batch-outreach/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      setResults(data.results || []);
      setStep('results');
    } catch { toast.error('Batch send failed'); }
    setSending(false);
  };

  const handleClose = () => {
    onComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="batch-outreach-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-gradient-to-br from-[#0A1F44] to-[#1a3a6b] rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#0A1F44]">Batch Outreach</h2>
              <p className="text-[10px] text-gray-500">
                {step === 'preflight' && `${selectedCompanies.length} companies selected`}
                {step === 'review' && `Reviewing ${drafts.length} sequences`}
                {step === 'confirm' && `Ready to send ${approvedSet.size} sequences`}
                {step === 'results' && `Batch complete`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Step indicators */}
            {['preflight', 'review', 'results'].map((s, i) => (
              <div key={s} className={`w-2 h-2 rounded-full ${step === s || ['preflight','review','results'].indexOf(step) > i ? 'bg-[#D4AF37]' : 'bg-gray-300'}`} />
            ))}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg ml-2" data-testid="close-batch">
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Running preflight checks...</span>
            </div>
          ) : step === 'preflight' ? (
            <SelectionSummary preflight={preflight} onRemove={removePreflight} onProceed={handleGenerate} generating={generating} />
          ) : step === 'review' ? (
            <ReviewQueue
              drafts={drafts}
              onApprove={handleApprove}
              onSkip={handleSkip}
              onEdit={() => {}}
              onRegenerate={handleRegenerate}
              expandedDraft={expandedDraft}
              setExpandedDraft={setExpandedDraft}
              approvedSet={approvedSet}
              skippedSet={skippedSet}
              tone={tone}
              setTone={setTone}
              steps={numSteps}
              setSteps={setNumSteps} />
          ) : step === 'results' ? (
            <ResultsSummary results={results} onClose={handleClose} />
          ) : null}
        </div>

        {/* Footer (review step only) */}
        {step === 'review' && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <button onClick={() => setStep('preflight')}
                className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700">Back</button>
              <button onClick={handleApproveAll}
                className="px-3 py-2 text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100"
                data-testid="approve-all-btn">
                <Check className="h-3 w-3 inline mr-1" />Approve All
              </button>
            </div>
            <button onClick={handleSendApproved}
              disabled={approvedSet.size === 0 || sending}
              className="px-5 py-2.5 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold rounded-lg hover:bg-[#c5a030] disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
              data-testid="send-approved-btn">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send {approvedSet.size} Approved
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
